import React, { useEffect, useState } from "react";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EyeIcon from "@material-ui/icons/RemoveRedEye";
import EmailIcon from "@material-ui/icons/Email";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Dialog, DialogActions, DialogTitle, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageSubscriptionUsersView.styles";
import SubscriptionUserModel from "../../../models/subscription/SubscriptionUserModel";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import * as MessageAction from "../../../stores/message/MessageAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import SubscriptionModel from "../../../models/subscription/SubscriptionModel";
import { useHistory, useParams } from "react-router-dom";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { CsvExportButton } from "../../components/CsvExport/CsvExportButton";
import { today } from "../../../utilities/dateTimeUtils";
import { SubscriptionUserDateRangesDialog } from "./SubscriptionUserDateRangesDialog";
import { SubscriptionVisibilityDateDialog } from "./SubscriptionVisibilityDateDialog";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import { BlueButton } from "../../components/Buttons/BlueButton";

export interface IManageSubscriptionUsersViewProps extends WithStyles<typeof styles> {}

const ManageSubscriptionUsersViewPure: React.FunctionComponent<IManageSubscriptionUsersViewProps> = (
    props: IManageSubscriptionUsersViewProps
) => {
    const classes = props.classes;
    const { subscriptionId } = useParams();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();
    const [showDateRangesDialog, setShowDateRangesDialog] = useState<boolean>(false);
    const [showVisibilityDateDialog, setShowVisibilityDateDialog] = useState<boolean>(false);
    const [editedUserIndex, setEditedUserIndex] = useState<number>(null);
    const [isEmailToUserDialogOpen, setIsEmailToUserDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        dispatch(SubscriptionAction.requestAllSubscriptionTypes());
        dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionId));
        dispatch(UserDetailsAction.requestAllUsersDetails());
    }, [subscriptionId, dispatch]);

    const subscriptionsFromState = useSelector(state => state.subscriptions);
    const isRequestingSubscriptions = useSelector(state =>
        selectRequesting(state, [
            SubscriptionAction.REQUEST_SUBSCRIPTION_USERS,
            SubscriptionAction.REQUEST_ALL_SUBSCRIPTIONS
        ])
    );
    const isRequestingUsers = useSelector(state =>
        selectRequesting(state, [
            SubscriptionAction.REQUEST_SUBSCRIPTION_USERS,
            UserDetailsAction.REQUEST_ALL_USERS_DETAILS
        ])
    );

    const getSubscriptionStatus = (subscriptionUser: SubscriptionUserModel): string => {
        if (subscriptionUser.isSubscriptionUpcoming) {
            return "Upcoming";
        } else {
            if (subscriptionUser.isSubscriptionUnrestricted) return t("admin.manageSubscriptionUserView.unrestricted");
            return subscriptionUser.isSubscriptionActive
                ? t("admin.manageSubscriptionUserView.active")
                : t("admin.manageSubscriptionUserView.nonactive");
        }
    };

    let subscriptionUsers: IUniversalTableData;
    if (subscriptionsFromState?.subscriptionUsers) {
        subscriptionUsers = Object.values(subscriptionsFromState?.subscriptionUsers)?.map(
            (subscriptionUser: SubscriptionUserModel) => ({
                userId: subscriptionUser.userId,
                firstName: subscriptionUser.firstName,
                lastName: subscriptionUser.lastName,
                email: subscriptionUser.email,
                additionalEmail2: subscriptionUser.additionalEmail2,
                additionalEmail3: subscriptionUser.additionalEmail3,
                company: subscriptionUser.company,
                subscriptionStatus: getSubscriptionStatus(subscriptionUser),
                trial: subscriptionUser.trial,
                salesTrial: subscriptionUser.salesTrial,
                isActiveOrUnrestricted:
                    subscriptionUser.isSubscriptionUnrestricted || subscriptionUser.isSubscriptionActive,
                visibilityStartDate: subscriptionUser.visibilityStartDate,
                visibilityEndDate: subscriptionUser.visibilityEndDate
            })
        );
    }

    const allUsersFromState = useSelector(state => state.userDetails?.all);
    let addableUsers: IUniversalTableData;
    if (allUsersFromState) {
        addableUsers = Object.values(allUsersFromState)?.map((user: UserDetailsModel) => ({
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            company: user.company,
            ...(user.userLocked && {
                actionsDisabled: ["addUser"]
            })
        }));
    }
    if (subscriptionsFromState?.subscriptionUsers) {
        addableUsers = addableUsers?.filter(
            user => !subscriptionUsers?.find(subscriptionUser => subscriptionUser.userId === user.userId)
        );
    }

    let currentSubscription: any;
    if (subscriptionsFromState?.allSubscriptions) {
        currentSubscription = Object.values(subscriptionsFromState?.allSubscriptions)?.find(
            (subscription: SubscriptionModel) => subscription.id === parseInt(subscriptionId)
        );
    }

    const subscriptionName = currentSubscription ? currentSubscription?.name : "";
    const subscriptionFullTitle = currentSubscription
        ? `${currentSubscription?.name} - ${currentSubscription?.description}`
        : "";
    const subscriptionIsFilterable = currentSubscription ? currentSubscription?.isFilterable : false;

    const handleDateRangesClick = (index: number) => {
        setEditedUserIndex(index);
        setShowDateRangesDialog(true);
    };

    const handleSendEmailToUserClick = index => {
        setEditedUserIndex(index);
        setIsEmailToUserDialogOpen(true);
    };

    const handleDeleteFinished = index => {
        dispatch(SubscriptionAction.deleteSubscriptionUser(subscriptionId, subscriptionUsers[index].userId)).then(
            () => {
                dispatch(SubscriptionAction.requestAllSubscriptionTypes());
                dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionId));
                enqueueSnackbar(t("admin.manageSubscriptionUserView.subscriptionUserDeleted"), {
                    variant: ToastStatusEnum.Info
                });
            }
        );
    };

    const handleAddUser = index => {
        dispatch(SubscriptionAction.addSubscriptionUser(subscriptionId, addableUsers[index]?.userId)).then(() => {
            dispatch(SubscriptionAction.requestAllSubscriptionTypes());
            dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionId));
            enqueueSnackbar(t("admin.manageSubscriptionUserView.subscriptionUserAdded"), {
                variant: ToastStatusEnum.Info
            });
        });
    };

    const handleVisibilityDateClick = (index: number) => {
        setEditedUserIndex(index);
        setShowVisibilityDateDialog(true);
    };

    const handleCloseVisibilityDateDialog = () => {
        setShowVisibilityDateDialog(false);
        setEditedUserIndex(null);
    };

    const handleSendEmailToUser = () => {
        setIsEmailToUserDialogOpen(false);
        const userId = subscriptionUsers[editedUserIndex]?.userId;
        dispatch(MessageAction.sendEmailToSubscriptionUser(userId, parseInt(subscriptionId)))
            .then(() => {
                enqueueSnackbar("Email has been sent to the user", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("An error has occured during sending the email: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleCloseDateRangesDialog = (isUpdated: boolean) => {
        setShowDateRangesDialog(false);
        setEditedUserIndex(null);
        if (isUpdated) {
            dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionId));
        }
    };

    const handleSendEmailToActiveUsers = index => {
        const path = `/admin/manage-subscriptions/${subscriptionId}/sendEmail`;
        history.push(path);
    };

    const handleUserEdit = index => {
        const path = "/admin/manage-users/" + subscriptionUsers[index].userId;
        history.push(path);
    };

    const exportMapping = [
        {
            label: t("general.email"),
            key: "email"
        },
        {
            label: "Email 2",
            key: "additionalEmail2"
        },
        {
            label: "Email 3",
            key: "additionalEmail3"
        },
        {
            label: t("general.firstName"),
            key: "firstName"
        },
        {
            label: t("general.lastName"),
            key: "lastName"
        },
        {
            label: "Company",
            key: "company"
        },
        {
            label: t("admin.manageSubscriptionUserView.subscriptionStatus"),
            key: "subscriptionStatus"
        },
        {
            label: "Trial",
            key: "trial",
            mappingFunc: value => (value ? "Yes" : "No")
        },
        {
            label: "Sales trial",
            key: "salesTrial",
            mappingFunc: value => (value ? "Yes" : "No")
        }
    ];

    const subscriptionsTablecolumns: IUniversalTableColumn[] = [
        {
            type: "link",
            title: t("general.email"),
            field: "email",
            onClick: index => handleUserEdit(index)
        },
        {
            type: "text",
            title: t("general.firstName"),
            field: "firstName"
        },
        {
            type: "text",
            title: t("general.lastName"),
            field: "lastName"
        },
        {
            type: "text",
            title: "Company",
            field: "company"
        },
        {
            type: "text",
            title: t("admin.manageSubscriptionUserView.subscriptionStatus"),
            field: "subscriptionStatus"
        },
        {
            type: "boolean",
            title: "Trial",
            field: "trial"
        },
        {
            type: "boolean",
            title: "Sales trial",
            field: "salesTrial"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "sendEmail",
                    icon: <EmailIcon />,
                    tooltip: "Send automated welcome email",
                    onClick: index => handleSendEmailToUserClick(index),
                    scope: ["subscriptions.users.write"]
                },
                {
                    name: "visibilityDate",
                    icon: <EyeIcon />,
                    tooltip: "Subscription visibility",
                    onClick: index => handleVisibilityDateClick(index),
                    scope: ["subscriptions.users.write"]
                },
                {
                    name: "dateRanges",
                    icon: <DateRangeIcon />,
                    tooltip: t("admin.manageSubscriptionUserView.userDateRanges"),
                    onClick: index => handleDateRangesClick(index),
                    scope: ["subscriptions.users.write"]
                },
                {
                    name: "delete",
                    tooltip: t("general.delete"),
                    disabledTooltip: "This user has subscription(s)",
                    onDeleteFinished: index => handleDeleteFinished(index),
                    deleteQuestion: t("admin.manageSubscriptionUserView.deleteQuestion"),
                    scope: ["subscriptions.users.write"]
                }
            ]
        }
    ];

    const usersTableColumns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.firstName"),
            field: "firstName"
        },
        {
            type: "text",
            title: t("general.lastName"),
            field: "lastName"
        },
        {
            type: "text",
            title: t("general.email"),
            field: "email"
        },
        {
            type: "text",
            title: "Company",
            field: "company"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "addUser",
                    icon: <PersonAddIcon />,
                    tooltip: t("admin.manageSubscriptionUserView.addUser"),
                    onClick: index => handleAddUser(index),
                    disabledTooltip: "Can't assign a subscription to a locked user"
                }
            ]
        }
    ];

    const selectedUser = subscriptionUsers?.[editedUserIndex];
    const userId = (selectedUser?.userId as string) || null;
    const userFullName = selectedUser ? `${selectedUser?.firstName} ${selectedUser?.lastName}` : "";

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    {
                        title: t("admin.manageSubscriptionsView.manageSubscriptions"),
                        href: "/admin/manage-subscriptions"
                    },
                    { title: `${t("admin.manageSubscriptionUserView.manageSubscriptionUsers")} - ${subscriptionName}` }
                ]}
            />
            <div className={classes.subscriptionSection}>
                <div className={classes.title}>
                    <Typography variant="h4">{subscriptionFullTitle}</Typography>
                    <Typography variant="h5">{t("admin.manageSubscriptionUserView.usersAssigned")}</Typography>
                </div>
                <div className={classes.exportButton}>
                    <CsvExportButton
                        data={subscriptionUsers?.filter(user => user.isActiveOrUnrestricted) || []}
                        outputFilename={`subscription_${subscriptionName}_${today().format("YYYYMMDD")}.csv`}
                        mappings={exportMapping}
                    >
                        {t("admin.manageSubscriptionUserView.exportActiveUsers")}
                    </CsvExportButton>
                    <BlueButton style={{ margin: 0 }} onClick={handleSendEmailToActiveUsers}>
                        Send email to active users
                    </BlueButton>
                </div>
                <UniversalTable
                    columns={subscriptionsTablecolumns}
                    data={subscriptionUsers}
                    isLoading={isRequestingSubscriptions}
                    searchable
                />
            </div>

            <PermissionGuard scope={["subscriptions.users.write"]}>
                <>
                    <div className={classes.title}>
                        <Typography variant="h5" gutterBottom>
                            {t("admin.manageSubscriptionUserView.addNewUsers")}
                        </Typography>
                    </div>
                    {currentSubscription?.isFilterable ? (
                        <Typography variant="body2">
                            Users can be assigned to a filterable subscription only via Subscription request!
                        </Typography>
                    ) : (
                        <UniversalTable
                            columns={usersTableColumns}
                            data={addableUsers}
                            isLoading={isRequestingUsers}
                            searchable
                        />
                    )}
                </>
            </PermissionGuard>

            {showDateRangesDialog && (
                <SubscriptionUserDateRangesDialog
                    subscriptionId={parseInt(subscriptionId)}
                    subscriptionTitle={subscriptionFullTitle}
                    subscriptionComment={currentSubscription?.comment}
                    userId={userId}
                    userName={userFullName}
                    onClose={isUpdated => handleCloseDateRangesDialog(isUpdated)}
                    isFilterable={subscriptionIsFilterable}
                />
            )}
            <Dialog open={isEmailToUserDialogOpen}>
                <DialogTitle id="confirm-email-dialog">
                    Are you sure you want to send the automated welcome email to the user?
                </DialogTitle>
                <DialogActions>
                    <BlueButton onClick={handleSendEmailToUser}>Yes</BlueButton>
                    <BlueButton onClick={() => setIsEmailToUserDialogOpen(false)}>No</BlueButton>
                </DialogActions>
            </Dialog>

            {showVisibilityDateDialog && (
                <SubscriptionVisibilityDateDialog
                    subscriptionTypeId={parseInt(subscriptionId)}
                    subscriptionTitle={subscriptionFullTitle}
                    userId={userId}
                    userName={userFullName}
                    onClose={() => handleCloseVisibilityDateDialog()}
                />
            )}
        </CenteredContainer>
    );
};

export const ManageSubscriptionUsersView = withStyles(styles)(ManageSubscriptionUsersViewPure);
